import { Schema } from 'effect';

import { HydraViewToHydraViewExtended } from './hydraView';
import type { StripJsonLd } from '../transform/withoutJsonLd';

/**
 * Generic Hydra Collection Response Schema
 * Wraps the entire JSON-LD response with hydra properties
 *
 * @param memberSchema - Schema for validating hydra:member array items
 */
const hydraCollectionCache = new WeakMap<Schema.Top, Schema.Top>();

export const HydraCollectionResponse = <S extends Schema.Top>(memberSchema: S) => {
  const cached = hydraCollectionCache.get(memberSchema);
  if (cached) return cached as ReturnType<typeof makeHydraCollectionResponse<S>>;

  const result = makeHydraCollectionResponse(memberSchema);
  hydraCollectionCache.set(memberSchema, result);
  return result;
};

const makeHydraCollectionResponse = <S extends Schema.Top>(memberSchema: S) =>
  Schema.Struct({
    '@context': Schema.optional(
      Schema.Union([Schema.String, Schema.Record(Schema.String, Schema.Unknown)])
    ),
    '@id': Schema.String,
    '@type': Schema.String,
    'hydra:member': Schema.Array(memberSchema),
    'hydra:totalItems': Schema.Finite,
    'hydra:view': Schema.optional(HydraViewToHydraViewExtended),
  }).annotate({ identifier: 'HydraCollectionResponse' });

export type HydraCollectionResponseType<T extends Schema.Top> = StripJsonLd<
  Schema.Schema.Type<ReturnType<typeof HydraCollectionResponse<T>>>
>;
